{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "$id": "appearance",
  "x-cli-editable": true,
  "title": "Appearance",
  "description": "Visual presentation: brand color, z-index, disclaimers, widget styling.",
  "type": "object",
  "properties": {
    "brand_color": {
      "ui:sectionTitle": "Branding",
      "ui:sectionDescription": "Colors and layout of the chat widget.",
      "ui:sectionIcon": "Palette",
      "type": "string",
      "ui:widget": "color",
      "ui:description": "Primary brand color (hex, e.g. #0A42C4)",
      "pattern": "^#[0-9A-Fa-f]{6}$"
    },
    "z_index": {
      "type": "integer",
      "x-tier": "advanced",
      "ui:description": "CSS z-index for the widget",
      "minimum": 0,
      "maximum": 999999
    },
    "enable_copilot_layout": {
      "type": "boolean",
      "ui:description": "Allow users to toggle between expanded and copilot (side panel) layouts",
      "x-access": "staff"
    },
    "input_placeholder": {
      "type": "object",
      "ui:title": "Input Placeholder Text",
      "ui:tooltip": "Custom text shown in the chat input bar before the visitor types anything.",
      "ui:description": "Overrides the default placeholder per language code (e.g. en, fr). Leave a language out to keep the default wording for it.",
      "x-tier": "advanced",
      "ui:placeholderDefaults": {
        "en": "Ask anything about {{company_name}}",
        "fr": "Posez-nous vos questions sur {{company_name}}",
        "es": "Pregúntanos sobre {{company_name}}",
        "it": "Chiedici di {{company_name}}",
        "de": "Frag uns zu {{company_name}}",
        "pt": "Pergunte qualquer coisa sobre {{company_name}}",
        "bg": "Попитайте всичко за {{company_name}}",
        "nl": "Vraag alles over {{company_name}}",
        "ro": "Întrebați orice despre {{company_name}}",
        "hu": "Kérdezzen bármit a(z) {{company_name}} vállalatról"
      },
      "additionalProperties": {
        "type": "string",
        "maxLength": 100
      }
    },
    "disclaimers": {
      "type": "object",
      "x-tier": "advanced",
      "ui:description": "Disclaimer display settings",
      "properties": {
        "always_show": {
          "type": "boolean",
          "ui:description": "Always show disclaimers (not just on first message)"
        },
        "text": {
          "type": "object",
          "ui:description": "Disclaimer text by language code",
          "additionalProperties": {
            "type": "object",
            "properties": {
              "ai": {
                "type": "string",
                "ui:description": "AI disclaimer text",
                "maxLength": 500
              },
              "poweredBy": {
                "type": "string",
                "ui:description": "Powered-by text",
                "maxLength": 200
              }
            }
          }
        }
      }
    },
    "widget_theme": {
      "type": "object",
      "ui:title": "Widget Styling",
      "ui:description": "Advanced semantic styling overrides for the website widget. These fields map to existing widget theme tokens and surfaces; leave them empty to keep the system defaults.",
      "x-tier": "advanced",
      "properties": {
        "text_primary": {
          "type": [
            "string",
            "null"
          ],
          "ui:widget": "color",
          "ui:title": "Primary text color",
          "ui:description": "Main widget text color, including chat body copy, typed input text, dynamic-question pill labels, and other surfaces that already use the primary text token.",
          "ui:group": "Colors",
          "pattern": "^#[0-9A-Fa-f]{6}$"
        },
        "text_secondary": {
          "type": [
            "string",
            "null"
          ],
          "ui:widget": "color",
          "ui:title": "Secondary text color",
          "ui:description": "Supporting widget text/icon color for roles already using the secondary text token, such as dynamic-question arrows, follow-up/suggested-answer arrows, and supporting popup copy. Does not affect muted header icons or primary CTA text.",
          "ui:group": "Colors",
          "pattern": "^#[0-9A-Fa-f]{6}$"
        },
        "text_placeholder": {
          "type": [
            "string",
            "null"
          ],
          "ui:widget": "color",
          "ui:title": "Placeholder text color",
          "ui:description": "Placeholder-like widget text, including search placeholder copy, minimized ask label, and the idle send-arrow state.",
          "ui:group": "Colors",
          "pattern": "^#[0-9A-Fa-f]{6}$"
        },
        "text_disclaimer": {
          "type": [
            "string",
            "null"
          ],
          "ui:widget": "color",
          "ui:title": "Disclaimer text color",
          "ui:description": "Disclaimer copy, powered-by text, and the Rose logo watermark.",
          "ui:group": "Colors",
          "pattern": "^#[0-9A-Fa-f]{6}$"
        },
        "icon_muted": {
          "type": [
            "string",
            "null"
          ],
          "ui:widget": "color",
          "ui:title": "Muted icon color",
          "ui:description": "Neutral muted icons such as header controls, close buttons, and other idle icon roles.",
          "ui:group": "Colors",
          "pattern": "^#[0-9A-Fa-f]{6}$"
        },
        "font_family": {
          "type": [
            "string",
            "null"
          ],
          "ui:title": "Font family",
          "ui:description": "Preferred widget font family name. It must already be available on the customer website or in the browser. Falls back to the widget default font stack when unavailable.",
          "ui:group": "Typography",
          "ui:subgroup": "Body",
          "maxLength": 120,
          "pattern": "^[^,;{}\\n\\r]+$"
        },
        "cta_font_family": {
          "type": [
            "string",
            "null"
          ],
          "ui:title": "CTA font family",
          "ui:description": "Preferred font family for conversion CTA buttons only, such as the CTA group above the search bar or inline CTA buttons in chat. Does not affect dynamic-question pills. The font must already be available on the customer website or in the browser.",
          "ui:group": "Typography",
          "ui:subgroup": "CTA",
          "maxLength": 120,
          "pattern": "^[^,;{}\\n\\r]+$"
        },
        "body_tracking_pct": {
          "type": [
            "number",
            "null"
          ],
          "ui:title": "Body tracking (%)",
          "ui:description": "Letter spacing for body copy, expressed as a percentage of font size. Example: 2 becomes 0.02em.",
          "ui:group": "Typography",
          "ui:subgroup": "Body",
          "minimum": -100,
          "maximum": 100
        },
        "body_leading_pct": {
          "type": [
            "number",
            "null"
          ],
          "ui:title": "Body leading (%)",
          "ui:description": "Line height for body copy, expressed as a percentage. Example: 150 becomes 150%.",
          "ui:group": "Typography",
          "ui:subgroup": "Body",
          "minimum": 50,
          "maximum": 300
        },
        "cta_tracking_pct": {
          "type": [
            "number",
            "null"
          ],
          "ui:title": "CTA tracking (%)",
          "ui:description": "Letter spacing for conversion CTA button labels only, expressed as a percentage of font size. Example: 2 becomes 0.02em.",
          "ui:group": "Typography",
          "ui:subgroup": "CTA",
          "minimum": -100,
          "maximum": 100
        },
        "cta_leading_pct": {
          "type": [
            "number",
            "null"
          ],
          "ui:title": "CTA leading (%)",
          "ui:description": "Line height for conversion CTA button labels only, expressed as a percentage. Example: 125 becomes 125%.",
          "ui:group": "Typography",
          "ui:subgroup": "CTA",
          "minimum": 50,
          "maximum": 300
        },
        "body_font_weight": {
          "type": [
            "integer",
            "null"
          ],
          "ui:title": "Body font weight",
          "ui:description": "Preferred font weight for regular body copy.",
          "ui:group": "Typography",
          "ui:subgroup": "Body",
          "minimum": 1,
          "maximum": 1000
        },
        "body_strong_font_weight": {
          "type": [
            "integer",
            "null"
          ],
          "ui:title": "Body bold font weight",
          "ui:description": "Preferred font weight for bold or emphasized body copy such as markdown **bold** text in assistant responses. Does not affect popup headings or other non-body strong UI.",
          "ui:group": "Typography",
          "ui:subgroup": "Body",
          "minimum": 1,
          "maximum": 1000
        },
        "input_font_weight": {
          "type": [
            "integer",
            "null"
          ],
          "ui:title": "Input font weight",
          "ui:description": "Preferred font weight for live search input text.",
          "ui:group": "Typography",
          "ui:subgroup": "Input",
          "minimum": 1,
          "maximum": 1000
        },
        "placeholder_font_weight": {
          "type": [
            "integer",
            "null"
          ],
          "ui:title": "Placeholder font weight",
          "ui:description": "Preferred font weight for placeholder-like surfaces such as the search placeholder and minimized ask label.",
          "ui:group": "Typography",
          "ui:subgroup": "Input",
          "minimum": 1,
          "maximum": 1000
        },
        "primary_cta_font_weight": {
          "type": [
            "integer",
            "null"
          ],
          "ui:title": "Primary CTA font weight",
          "ui:description": "Preferred font weight for primary conversion CTA buttons, such as the first CTA shown above the search bar. When unset, those buttons keep their default weight.",
          "ui:group": "Typography",
          "ui:subgroup": "CTA",
          "minimum": 1,
          "maximum": 1000
        },
        "secondary_cta_font_weight": {
          "type": [
            "integer",
            "null"
          ],
          "ui:title": "Secondary CTA font weight",
          "ui:description": "Preferred font weight for secondary conversion CTA buttons, such as the lighter secondary button shown next to the primary CTA. When unset, those buttons keep their default weight.",
          "ui:group": "Typography",
          "ui:subgroup": "CTA",
          "minimum": 1,
          "maximum": 1000
        },
        "compact_shell_radius": {
          "type": [
            "integer",
            "null"
          ],
          "ui:title": "Compact shell radius (px)",
          "ui:description": "Rounded corner radius for outer compact glass shells such as the minimized and collapsed widget entry points.",
          "ui:group": "Radius",
          "minimum": 0,
          "maximum": 9999
        },
        "compact_control_radius": {
          "type": [
            "integer",
            "null"
          ],
          "ui:title": "Compact control radius (px)",
          "ui:description": "Rounded corner radius for compact controls inside compact shells, such as the minimized ask button and the compact search input.",
          "ui:group": "Radius",
          "minimum": 0,
          "maximum": 9999
        },
        "glass_effect_start_alpha_pct": {
          "type": [
            "number",
            "null"
          ],
          "ui:title": "Glass effect start alpha (%)",
          "ui:description": "Alpha percentage for the start stop of the widget glass effect gradient. Example: 30 matches the current 0.30 alpha.",
          "ui:group": "Glass Effect",
          "minimum": 0,
          "maximum": 100
        },
        "glass_effect_end_alpha_pct": {
          "type": [
            "number",
            "null"
          ],
          "ui:title": "Glass effect end alpha (%)",
          "ui:description": "Alpha percentage for the end stop of the widget glass effect gradient. Example: 18 matches the current 0.18 alpha.",
          "ui:group": "Glass Effect",
          "minimum": 0,
          "maximum": 100
        },
        "card_radius": {
          "type": [
            "integer",
            "null"
          ],
          "ui:title": "Card radius (px)",
          "ui:description": "Rounded corner radius for card-like widget surfaces such as question bubbles, popup cards, and evaluation buttons.",
          "ui:group": "Radius",
          "minimum": 0,
          "maximum": 9999
        },
        "cta_radius": {
          "type": [
            "integer",
            "null"
          ],
          "ui:title": "CTA radius (px)",
          "ui:description": "Rounded corner radius for conversion CTA buttons only. Dynamic-question pills and follow-up/suggested-answer pills stay on the shared pill radius.",
          "ui:group": "Radius",
          "minimum": 0,
          "maximum": 9999
        }
      }
    }
  },
  "default": {
    "brand_color": "#0A42C3",
    "z_index": 9999,
    "enable_copilot_layout": true,
    "input_placeholder": {
      "en": "Ask anything about {{company_name}}",
      "fr": "Posez-nous vos questions sur {{company_name}}",
      "es": "Pregúntanos sobre {{company_name}}",
      "it": "Chiedici di {{company_name}}",
      "de": "Frag uns zu {{company_name}}",
      "pt": "Pergunte qualquer coisa sobre {{company_name}}",
      "bg": "Попитайте всичко за {{company_name}}",
      "nl": "Vraag alles over {{company_name}}",
      "ro": "Întrebați orice despre {{company_name}}",
      "hu": "Kérdezzen bármit a(z) {{company_name}} vállalatról"
    },
    "disclaimers": {
      "always_show": false,
      "text": {
        "en": {
          "ai": "AI-generated replies",
          "poweredBy": "Powered by"
        },
        "fr": {
          "ai": "Réponses générées par IA",
          "poweredBy": "Propulsé par"
        }
      }
    },
    "widget_theme": {
      "text_primary": null,
      "text_secondary": null,
      "text_placeholder": null,
      "text_disclaimer": null,
      "icon_muted": null,
      "font_family": null,
      "cta_font_family": null,
      "body_tracking_pct": null,
      "body_leading_pct": null,
      "cta_tracking_pct": null,
      "cta_leading_pct": null,
      "body_font_weight": null,
      "body_strong_font_weight": null,
      "input_font_weight": null,
      "placeholder_font_weight": null,
      "primary_cta_font_weight": null,
      "secondary_cta_font_weight": null,
      "compact_shell_radius": null,
      "compact_control_radius": null,
      "glass_effect_start_alpha_pct": null,
      "glass_effect_end_alpha_pct": null,
      "card_radius": null,
      "cta_radius": null
    }
  }
}
